import React, {
    useState, useCallback, useContext, useMemo, useRef
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { WizardPage } from 'gw-portals-wizard-react';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import SgCepMotorAddons from 'sg-cep-motor-addons';
import { TranslatorContext } from '@jutro/locale';
import { SgCepChanges, SgQuoteContext } from 'saga-modules';
import SgCepSideMenu from 'sg-cep-side-menu';
import SpmConstants from 'sg-spm-constants';
import _ from 'lodash';
import './SgRenewalAddonsPage.messages';
import { RenewalService } from 'gw-capability-renewal';
import { Loader } from '@jutro/components';
import metadata from './SgRenewalAddonsPage.metadata.json5';

const pageId = 'RenewalAddonsPage';

function SgRenewalAddonsPage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        sgWizardPropsExt,
        jumpTo,
        goNext,
        steps
    } = props;

    const {
        handleCancel
    } = sgWizardPropsExt;

    const {
        onValidate,
        isComponentValid
    } = useValidation(pageId);

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const getValue = useCallback((value) => {
        const typelistValue = viewModelService.productMetadata.get('pc').types.getTypelist(value.typelist.name).getCode(value.code).name;
        return translator({ id: typelistValue, defaultMessage: typelistValue }).toString();
    }, [translator, viewModelService.productMetadata]);

    const { spmVehicle } = modelVM.lobData.privateMotor.coverables;

    const changes = SgCepChanges.getMotorChanges(modelVM, getValue, true);

    const volExcess = useRef(_.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code'));
    const [quoting, setQuoting] = useState(false);
    const [isLoading, setLoadingState] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const quoteContext = {
        quoting,
        setQuoting
    };

    const commonProps = {
        modelVM,
        updateData,
        showErrors,
        onValidate,
        messageParams: {
            vehicle: SpmConstants.CAR
        }
    };

    const continueRenewal = useCallback(() => {
        const declarationPageIndex = _.findIndex(
            steps,
            ({ path }) => path === '/declarations'
        );
        jumpTo(declarationPageIndex);
    }, [steps, jumpTo]);

    const hasExcessChanged = useMemo(() => {
        return volExcess.current !== _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code');
    }, [_.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code')]);

    const onContinue = useCallback(async () => {
        if (Object.keys(changes).length === 0 && !hasExcessChanged) {
            continueRenewal();
        } else {
            if (!_.isNil(isComponentValid) && !isComponentValid) {
                setShowErrors(true);
                return; // do not continue
            }
            modelVM.value.renewalDetails.isAmendingRenewal = true;
            setLoadingState(true);
            const result = await RenewalService.save(modelVM.value);
            const modelCopy = _.clone(modelVM);
            modelCopy.value = result;
            updateData(modelCopy);
            setLoadingState(false);
            goNext();
        }
    }, [modelVM, setShowErrors, changes]);

    const overrideProps = {
        vehicleSubHeader: {
            content: `${spmVehicle.value.year} ${spmVehicle.value.make} ${spmVehicle.value.model}`
        },
        cepMotorAddonsComponent: {
            ...commonProps
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
            handleCancel,
            onContinue,
            continueRenewal
        },
        resolveComponentMap: {
            SgCepMotorAddons,
            SgCepSideMenu
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <SgQuoteContext.Provider value={quoteContext}>
            <WizardPage showPrevious={false} showNext={false}>
                {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
            </WizardPage>
        </SgQuoteContext.Provider>
    );
}

export default SgRenewalAddonsPage;
